'use client';

import { c as _c } from "react/compiler-runtime";
import { Suspense, useEffect, useId } from 'react';
import { useSearchParams } from 'next/navigation';
import { twMerge } from 'tailwind-merge';
import { Button, FlexContainer, OverflowMenu } from '../components';
import { CollapsibleTableFilters } from './CollapsibleTableFilters';
import { getFilterKey } from './filter-active';
import TableFilter from './TableFilter';
import { useTableFilters } from './useTableFilters';
import type { ReactNode } from 'react';
import type { MenuItem } from '../components';
import type { TableFilterType } from './TableFilter';
import type { FilterColumnsConfig, FilterConfigMap } from './typed-filter-config';
export interface TableFiltersProps<T extends FilterConfigMap = FilterConfigMap> {
  config: FilterColumnsConfig;
  className?: string;
  showResetButton?: boolean;
  resetButtonPosition?: 'left' | 'right' | 'center';
  /**
   * The type of filter to use. rsc = router.replace, client = window.history.replace.
   * @default 'rsc'
   */
  filterType?: TableFilterType;
  /**
   * Optional menu items to render in an OverflowMenu in the right column.
   */
  overflowMenuItems?: MenuItem[];
  /**
   * Alignment of the overflow menu dropdown.
   * @default 'end'
   */
  overflowMenuAlign?: 'start' | 'center' | 'end';
  /**
   * Whether filters stretch to fill their group's width. `false` keeps them at
   * their natural width, packed to the left (no stretching or uneven gaps when
   * they wrap). Applies to both groups. Ignored when `collapsible` is set.
   * @default true
   */
  grow?: boolean;
  /**
   * Declutter the bar: show only filters that hold a non-default value (or were
   * added this session), with an "Add filter" menu for the rest and a remove (✕)
   * control on filters sitting at their default. Renders all filters as one
   * left-aligned group (ignores column layout). Give each filter a `label` for
   * the menu. See {@link CollapsibleTableFilters}.
   * @default false
   */
  collapsible?: boolean;
  /**
   * Filter keys (their `searchParam`) to always show when `collapsible` is set,
   * skipping "Add filter" and the remove (✕) control. Ignored otherwise.
   *
   * Typed against the filter config's own keys — pass the config's type as this
   * component's type argument (same convention as `createTableSuspenseWrapper`),
   * e.g. `<TableFilters<typeof shovelsFilterConfig> alwaysVisible={['search']} ... />`.
   */
  alwaysVisible?: (keyof T & string)[];
  /**
   * Extra controls rendered in the right group, between the right-column filters
   * and the reset button. Use for app-specific actions the shared component
   * shouldn't own (e.g. the dashboard's export button). Ignored when
   * `collapsible` is set.
   */
  rightActions?: ReactNode;
  /**
   * `'vertical'` stacks the groups, and the filters within each group, in one
   * column — for a filter panel in a narrow sidebar rather than a bar above a
   * table. Each filter gets its own row deterministically, instead of landing
   * wherever the horizontal layout's wrapping puts it. Ignored when `collapsible`
   * is set.
   * @default 'horizontal'
   */
  orientation?: 'horizontal' | 'vertical';
}
function TableFiltersInternal(t0) {
  const $ = _c(46);
  const {
    config,
    className,
    showResetButton: t1,
    resetButtonPosition: t2,
    filterType: t3,
    overflowMenuItems,
    overflowMenuAlign: t4,
    grow: t5,
    collapsible: t6,
    alwaysVisible,
    rightActions,
    orientation: t7
  } = t0;
  const showResetButton = t1 === undefined ? true : t1;
  const resetButtonPosition = t2 === undefined ? "right" : t2;
  const filterType = t3 === undefined ? "rsc" : t3;
  const overflowMenuAlign = t4 === undefined ? "end" : t4;
  const grow = t5 === undefined ? true : t5;
  const collapsible = t6 === undefined ? false : t6;
  const orientation = t7 === undefined ? "horizontal" : t7;
  const searchParams = useSearchParams();
  const ownerId = useId();
  const {
    activeFilterParams,
    updateActiveFiltersFromConfig,
    releaseActiveFilters,
    clearAllFilters
  } = useTableFilters(filterType);
  const leftColumnFilters = config.left;
  let t8;
  if ($[0] !== config.right) {
    t8 = config.right ?? [];
    $[0] = config.right;
    $[1] = t8;
  } else {
    t8 = $[1];
  }
  const rightColumnFilters = t8;
  let t10;
  let t9;
  if ($[2] !== config || $[3] !== ownerId || $[4] !== searchParams || $[5] !== updateActiveFiltersFromConfig) {
    t9 = () => {
      updateActiveFiltersFromConfig(config, searchParams, ownerId);
    };
    t10 = [config, searchParams, updateActiveFiltersFromConfig, ownerId];
    $[2] = config;
    $[3] = ownerId;
    $[4] = searchParams;
    $[5] = updateActiveFiltersFromConfig;
    $[6] = t10;
    $[7] = t9;
  } else {
    t10 = $[6];
    t9 = $[7];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[8] !== ownerId || $[9] !== releaseActiveFilters) {
    t11 = () => () => releaseActiveFilters(ownerId);
    t12 = [releaseActiveFilters, ownerId];
    $[8] = ownerId;
    $[9] = releaseActiveFilters;
    $[10] = t11;
    $[11] = t12;
  } else {
    t11 = $[10];
    t12 = $[11];
  }
  useEffect(t11, t12);
  if (collapsible) {
    let t13;
    if ($[12] !== alwaysVisible || $[13] !== className || $[14] !== config || $[15] !== filterType || $[16] !== showResetButton) {
      t13 = <CollapsibleTableFilters className={className} alwaysVisible={alwaysVisible} config={config} filterType={filterType} showResetButton={showResetButton} />;
      $[12] = alwaysVisible;
      $[13] = className;
      $[14] = config;
      $[15] = filterType;
      $[16] = showResetButton;
      $[17] = t13;
    } else {
      t13 = $[17];
    }
    return t13;
  }
  let t13;
  if ($[18] !== activeFilterParams || $[19] !== clearAllFilters || $[20] !== showResetButton) {
    t13 = showResetButton && activeFilterParams.length > 0 && <Button onClick={() => clearAllFilters()} size="md" variant="quaternary">Reset</Button>;
    $[18] = activeFilterParams;
    $[19] = clearAllFilters;
    $[20] = showResetButton;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const resetButton = t13;
  const isVertical = orientation === "vertical";
  const t14 = isVertical ? "flex-col items-stretch gap-3" : "flex-col gap-2 md:gap-4 md:flex-row md:items-center md:justify-between";
  let t15;
  if ($[22] !== className || $[23] !== t14) {
    t15 = twMerge(t14, className);
    $[22] = className;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== filterType || $[26] !== grow || $[27] !== isVertical || $[28] !== leftColumnFilters || $[29] !== resetButton || $[30] !== resetButtonPosition) {
    t16 = (leftColumnFilters.length > 0 || resetButtonPosition === "left" && resetButton) && <FlexContainer className={isVertical ? "flex-col items-stretch gap-2 w-full" : "flex-wrap gap-4 w-full md:w-auto"} items="center" shrink={true}>{resetButtonPosition === "left" && resetButton}{leftColumnFilters.map(props => <TableFilter key={getFilterKey(props)} {...props} filterType={filterType} grow={grow} />)}{resetButtonPosition === "center" && resetButton}</FlexContainer>;
    $[25] = filterType;
    $[26] = grow;
    $[27] = isVertical;
    $[28] = leftColumnFilters;
    $[29] = resetButton;
    $[30] = resetButtonPosition;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== filterType || $[33] !== grow || $[34] !== isVertical || $[35] !== overflowMenuAlign || $[36] !== overflowMenuItems || $[37] !== resetButton || $[38] !== resetButtonPosition || $[39] !== rightActions || $[40] !== rightColumnFilters) {
    t17 = (rightColumnFilters.length > 0 || resetButtonPosition === "right" && resetButton || rightActions || overflowMenuItems && overflowMenuItems.length > 0) && <FlexContainer className={isVertical ? "flex-col items-stretch gap-2 w-full" : "flex-wrap gap-2 md:gap-4 w-full md:w-auto self-start"} items="center">{rightColumnFilters.map(props_0 => <TableFilter key={getFilterKey(props_0)} {...props_0} filterType={filterType} grow={grow} />)}{rightActions}{resetButtonPosition === "right" && resetButton}{overflowMenuItems && overflowMenuItems.length > 0 && <OverflowMenu align={overflowMenuAlign} items={overflowMenuItems} />}</FlexContainer>;
    $[32] = filterType;
    $[33] = grow;
    $[34] = isVertical;
    $[35] = overflowMenuAlign;
    $[36] = overflowMenuItems;
    $[37] = resetButton;
    $[38] = resetButtonPosition;
    $[39] = rightActions;
    $[40] = rightColumnFilters;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t15 || $[43] !== t16 || $[44] !== t17) {
    t18 = <FlexContainer className={t15}>{t16}{t17}</FlexContainer>;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  return t18;
}
export function TableFilters(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <Suspense fallback={t0}><TableFiltersInternal {...props} /></Suspense>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export default TableFilters;